/***
*
*   EVENT/GROUPS
*   List the events grouped by name
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Input, Space, Typography, Button, Badge, Empty
} from 'antd';
import {
  ThunderboltOutlined, SearchOutlined, EyeOutlined, ReloadOutlined
} from '@ant-design/icons';
import { Animate } from 'components/lib';
import { fetchEventGroups, invalidateEventsCache } from '../../store/slices/eventsSlice';
// import { getCacheKey } from '../../store/cacheUtils';

const { Title, Text } = Typography;

export function EventGroups() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');

  const eventsState = useSelector((state) => state.events);
  const cacheKey = `groups_${searchInput}`;
  const groupsData = eventsState.groups[cacheKey];
  const loading = eventsState.groupsLoading;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchEventGroups(searchInput));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch, searchInput]);

  const handleRefresh = () => {
    dispatch(invalidateEventsCache());
    dispatch(fetchEventGroups(searchInput));
  };

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <ThunderboltOutlined style={{ color: '#8B5CF6' }} />
          <Button type="link" onClick={() => navigate(`/events/${record.name}`)}>
            {text}
          </Button>
        </Space>
      ),
    },
    {
      title: 'Total Triggers',
      dataIndex: 'total_triggers',
      key: 'total_triggers',
      render: (count) => <Badge count={count} showZero style={{ backgroundColor: '#8B5CF6' }} />,
      sorter: (a, b) => a.total_triggers - b.total_triggers,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/events/${record.name}`)}
        >
          View Events
        </Button>
      ),
    },
  ];

  const tableData = (groupsData?.results || []).map(item => ({
    ...item,
    key: item.name,
  }));

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              Event Groups
            </Title>
            <Text type="secondary">View all events grouped by name</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        {/* Search */}
        <Card style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search event groups..."
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
            style={{ maxWidth: 400 }}
          />
        </Card>

        {/* Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              pageSize: 25,
              showTotal: (total) => `Total ${total} event groups`,
            }}
            locale={{ emptyText: <Empty description="No event groups found" /> }}
          />
        </Card>
      </div>
    </Animate>
  );
}